<template>
  <div class="container mt-4 mb-5">
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border"></div>
      <p class="text-muted mt-2">Loading support call...</p>
    </div>
    <div v-else>
      <div v-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-if="call">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Support Call #{{ call.id }}</h5>
          <div>
            <button class="btn btn-primary btn-sm" @click="doPrint">Print</button>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-body">
            <div>
              <img :src="isLightMode ? require('@/assets/logo-nav.png') : require('@/assets/logo-nav-dark.png')" width="180" />
            </div>
            <table class="table table-bordered fSmall w-auto">
              <tbody>
                <tr>
                  <th>Date</th>
                  <td>{{ call.workDate ? new Date(call.workDate).toLocaleString() : '' }}</td>
                </tr>
                <tr>
                  <th>Job Name</th>
                  <td>{{ call.jobName }}</td>
                </tr>
                <tr>
                  <th>Tech</th>
                  <td>{{ call.tech }}</td>
                </tr>
                <tr v-if="call.companyName">
                  <th>Company</th>
                  <td>{{ call.companyName }}</td>
                </tr>
                <tr>
                  <th>Issue Reported</th>
                  <td>{{ call.issueReported }}</td>
                </tr>
                <tr>
                  <th>Description</th>
                  <td>{{ call.description }}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <template v-if="call.status==1">🚀 opened</template>
                    <template v-else-if="call.status==2">🧰 in repair</template>
                    <template v-else-if="call.status==3">✅ closed</template>
                    <template v-else>Unknown</template>
                  </td>
                </tr>
                <tr>
                  <th>Total hours</th>
                  <td>
                    <div v-if="call.historyCalls && call.historyCalls.length" class="mt-1">
                      {{ computeTotalHours(call.historyCalls) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <h6 class="mt-4" v-if="call.historyCalls && call.historyCalls.length">Details</h6>
            <table class="table table-sm table-striped" v-if="call.historyCalls && call.historyCalls.length">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Total hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in call.historyCalls" :key="h.id">
                  <td>-Call time</td>
                  <td>{{ h.startDate }}</td>
                  <td>{{ h.endDate }}</td>
                  <td>{{ Number(h.totalHours).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Print area layout -->
    <div class="container-fluid" id="print-area" v-if="call && showPrintArea">
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <div>
              <img :src="isLightMode ? require('@/assets/logo-nav.png') : require('@/assets/logo-nav-dark.png')" width="180" />
            </div>
            <div class="text-center">
              <img :src="qrData" alt="Generated QR" style="height: 150px; border-style: solid; border-width: 0.6rem;" />
              <div class="fw-bold" style="font-size: 1.5rem;">Support Call #{{ call.id }}</div>
            </div>
          </div>
          <h5>Support Call</h5>
          <table class="table table-bordered fSmall w-100">
            <tbody>
              <tr>
                <th>Date</th>
                <td>{{ call.workDate ? new Date(call.workDate).toLocaleString() : '' }}</td>
              </tr>
              <tr>
                <th>Job Name</th>
                <td>{{ call.jobName }}</td>
              </tr>
              <tr>
                <th>Tech</th>
                <td>{{ call.tech }}</td>
              </tr>
              <tr v-if="call.companyName">
                <th>Company</th>
                <td>{{ call.companyName }}</td>
              </tr>
              <tr>
                <th>Issue Reported</th>
                <td>{{ call.issueReported }}</td>
              </tr>
              <tr>
                <th>Description</th>
                <td>{{ call.description }}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>
                  <template v-if="call.status==1">🚀 opened</template>
                  <template v-else-if="call.status==2">🧰 in repair</template>
                  <template v-else-if="call.status==3">✅ closed</template>
                  <template v-else>Unknown</template>
                </td>
              </tr>
              <tr>
                <th>Total hours</th>
                <td>
                  <div v-if="call.historyCalls && call.historyCalls.length" class="mt-1">
                    {{ computeTotalHours(call.historyCalls) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <h6 class="mt-4" v-if="call.historyCalls && call.historyCalls.length">Details</h6>
          <table class="table table-sm table-striped" v-if="call.historyCalls && call.historyCalls.length">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Total hours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="h in call.historyCalls" :key="h.id">
                <td>-Call time</td>
                <td>{{ h.startDate }}</td>
                <td>{{ h.endDate }}</td>
                <td>{{ Number(h.totalHours).toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import printJS from 'print-js'
import QRCode from 'qrcode'
var config = require('@/config/api.json')
const API = config.api
const API2 = config.apiManager

export default {
  name: 'PrintRemoteCall',
  props: ['id'],
  data() {
    return {
      loading: true,
      error: '',
      call: null,
      qrData: null,
      isLightMode: false,
      showPrintArea: false
    }
  },
  created() {
    const light = localStorage.getItem('light')
    document.body.classList.toggle('light-mode', false)
    document.body.classList.toggle('light-mode', true)
    if (light !== null) {
      document.body.classList.toggle('light-mode', light)
      this.isLightMode = light
    } else {
      document.body.classList.toggle('light-mode', false)
    }
  },
  async mounted() {
    // Sync light mode state
    const light = localStorage.getItem('light')
    this.isLightMode = (light === 'true' || light === true)
    await this.loadCall()
  },
  methods: {
    async createQr() {
      try {
        const value = `${API2}/print-support-call/${encodeURIComponent(String(this.id))}`
        this.qrData = await QRCode.toDataURL(value)
      } catch (e) {
        this.qrData = null
      }
    },
    async loadCall() {
      try {
        if (!this.id) {
          this.error = 'Corrupt data: missing id.'
          this.loading = false
          return
        }
        const res = await axios.get(`${API}/get-supportCall-byId/${encodeURIComponent(this.id)}`)
        if (!res.data || typeof res.data !== 'object') {
          this.error = 'Corrupt data received.'
          this.loading = false
          return
        }
        this.call = res.data
        await this.createQr()
      } catch (e) {
        this.error = 'Error loading support call.'
      } finally {
        this.loading = false
      }
    },
    doPrint() {
      if (!this.call) return
      this.showPrintArea = true
      this.$nextTick(() => {
        setTimeout(() => {
          printJS({ printable: 'print-area', type: 'html', targetStyles: ['*'], scanStyles: true })
          setTimeout(() => { this.showPrintArea = false }, 50)
        }, 50)
      })
    },
    computeTotalHours(history) {
      try {
        const total = (history || []).reduce((acc, h) => acc + (Number(h.totalHours) || 0), 0)
        return total.toFixed(2)
      } catch (e) {
        return '0.00'
      }
    }
  }
}
</script>

<style>
#print-area table { width: 100%; table-layout: fixed; }
#print-area table th { width: 260px; vertical-align: top; }
#print-area table td { padding-left: 16px; white-space: nowrap; }
</style>

